import { useState } from 'react'
import { Ban, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useRejectCorrection } from '../../hooks/backend/shifts'
import type { PendingCorrection } from './reconcile'

// Deny is a normal, healthy outcome — especially for stale rows — so the
// trigger is a secondary (outline) button, never destructive-red. It opens a
// small confirm dialog with an optional notes field, then calls
// reject_shift_correction_v1 via the backend. On success the caller refreshes
// the pending list and both source queues.
export function DenyCorrectionDialog({
  row,
  onDenied,
}: {
  row: PendingCorrection
  onDenied: () => void
}) {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { trigger, loading } = useRejectCorrection()

  async function handleDeny() {
    setError(null)
    try {
      const trimmed = notes.trim()
      await trigger(
        { correction_id: row.id, notes: trimmed.length > 0 ? trimmed : null },
        { skipCache: true },
      ).result
      toast.success('Correction denied.')
      setOpen(false)
      setNotes('')
      onDenied()
    } catch (err) {
      // Surface the DB function's refusal message verbatim.
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null)
        setOpen(next)
      }}
    >
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        <Ban className="h-3.5 w-3.5" />
        Deny
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Deny this correction</DialogTitle>
          <DialogDescription>
            The proposal is marked rejected and any pending owner adjustment attached to it is
            cancelled. Nothing about the shift changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* The change being denied, worded from the field. */}
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{row.field || 'value'}</span>
              <span className="font-mono font-semibold text-foreground">
                {row.from_value || '—'}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono font-semibold text-foreground">
                {row.to_value || '—'}
              </span>
            </div>
            {row.is_stale === true && row.stale_reason ? (
              <div className="mt-2 flex items-start gap-2">
                <Chip tone="amber">Stale</Chip>
                <span className="text-xs leading-relaxed text-muted-foreground">
                  {row.stale_reason}
                </span>
              </div>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="deny-notes" className="text-xs font-medium text-muted-foreground">
              Notes (optional)
            </Label>
            <Textarea
              id="deny-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why is this being denied? Optional."
              rows={3}
              className="text-sm"
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handleDeny} disabled={loading}>
            {loading && <Spinner className="mr-1.5 h-4 w-4" />}
            <Ban className="mr-1.5 h-4 w-4" />
            Deny correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
